import { FunctionComponent, forwardRef, PropsWithoutRef, Ref, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { ForwardProps, BaseProps, AsProp } from '../../types';
import { defaultThemeProp } from '../../theme';

export interface FlexContainerProps {
  /** Display as inline-flex. */
  inline?: boolean;
  /** Padding on the container represented as a multiplier or set of multipliers to the base spacing value in the theme. */
  pad?:
    | number
    | [topRightBottomLeft: number]
    | [topBottom: number, rightLeft: number]
    | [top: number, rightLeft: number, bottom: number]
    | [top: number, right: number, bottom: number, left: number];
  /** [flex-direction](https://css-tricks.com/almanac/properties/f/flex-direction/) */
  direction?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  /** [justify-content](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content) */
  justify?: 'start' | 'end' | 'center' | 'between' | 'around' | 'evenly' | 'stretch';
  /** [flex-wrap](https://css-tricks.com/almanac/properties/f/flex-wrap/) */
  wrap?: 'wrap' | 'nowrap' | 'wrap-reverse';
  /** [align-items](https://css-tricks.com/almanac/properties/a/align-items/). */
  alignItems?: 'stretch' | 'start' | 'end' | 'center' | 'baseline';
  /** [align-content](https://css-tricks.com/almanac/properties/a/align-content/) */
  alignContent?: 'start' | 'end' | 'center' | 'between' | 'around' | 'stretch';
  /**
   * Defines how much equal space to place between items represented as a multiplier to the base spacing value in the theme.
   * @deprecated
   */
  itemGap?: number;
  /**
   * Defines how much equal space to place between columns represented as a multiplier to the base spacing value in the theme.
   * [column-gap](https://css-tricks.com/almanac/properties/c/column-gap/)
   */
  colGap?: number;
  /**
   * Defines how much equal space to place between rows represented as a multiplier to the base spacing value in the theme.
   * [row-gap](https://css-tricks.com/almanac/properties/r/row-gap/)
   */
  rowGap?: number;
  /**
   * Defines how much equal space to place between both rows and columns represented as a multiplier to the base spacing value in the theme.
   * [gap](https://css-tricks.com/almanac/properties/g/gap/)
   */
  gap?: number;
}

export interface FlexItemProps {
  /** [align-self](https://css-tricks.com/almanac/properties/a/align-self/) */
  alignSelf?: 'auto' | 'start' | 'end' | 'baseline' | 'center' | 'stretch';
  /** [flex-grow](https://css-tricks.com/almanac/properties/f/flex-grow/) */
  grow?: number;
  /** [flex-shrink](https://css-tricks.com/almanac/properties/f/flex-shrink/) */
  shrink?: number;
  /** [flex-basis](https://css-tricks.com/almanac/properties/f/flex-basis/) */
  basis?: string;
}

interface BaseFlexProps {
  container?: FlexContainerProps | true;
  item?: FlexItemProps;
}

export interface FlexProps extends BaseFlexProps, BaseProps, AsProp {
  /** Content for either a container or item. */
  children?: ReactNode;
  /** Ref for the flex element. */
  ref?: Ref<Element>;
  /** Override props at extra small screen sizes and above. */
  xs?: BaseFlexProps;
  /** Override props at small screen sizes and above. */
  sm?: BaseFlexProps;
  /** Override props at medium screen sizes and above. */
  md?: BaseFlexProps;
  /** Override props at large screen sizes and above. */
  lg?: BaseFlexProps;
  /** Override props at extra large screen sizes and above. */
  xl?: BaseFlexProps;
}

const prefix = (value: string) => {
  if (['between', 'around', 'evenly'].includes(value)) return `space-${value}`;
  if (['start', 'end'].includes(value)) return `flex-${value}`;
  return value;
};

const getContainerStyles = (container: FlexProps['container']) => {
  if (!container) return '';

  if (container === true) {
    return css`
      display: flex;
    `;
  }

  if (container && typeof container === 'object') {
    const isRow = !container.direction || container.direction.includes('row');
    const isReverse = container.direction && container.direction.includes('reverse');

    return css`
      display: ${container.inline ? 'inline-flex' : 'flex'};

      ${container.pad !== undefined &&
      css`
        /* stylelint-disable function-name-case, function-whitespace-after */
        padding: ${({
          theme: {
            base: { spacing }
          }
        }) =>
          (Array.isArray(container.pad) ? container.pad : [container.pad])
            .map(p => `calc(${p} * ${spacing})`)
            .join(' ')};

        /* stylelint-enable function-name-case, function-whitespace-after */
      `}

      ${container.direction &&
      css`
        flex-direction: ${container.direction};
      `}

      ${container.justify &&
      css`
        justify-content: ${prefix(container.justify)};
      `}

      ${container.wrap &&
      css`
        flex-wrap: ${container.wrap};
      `}

      ${container.alignItems &&
      css`
        align-items: ${prefix(container.alignItems)};
      `}

      ${container.alignContent &&
      css`
        align-content: ${prefix(container.alignContent)};
      `}

      ${container.itemGap &&
      css`
          > * {
            margin-${isRow ? 'inline-start' : 'block-start'}: calc(${container.itemGap} * ${props =>
        props.theme.base.spacing});
            ${isReverse ? ':last-child' : ':first-child'} {
              margin-${isRow ? 'inline-start' : 'block-start'}: 0;
            }
          }
        `}

      ${container.gap !== undefined &&
      css`
        gap: calc(${container.gap} * ${props => props.theme.base.spacing});
      `}

      ${container.colGap !== undefined &&
      css`
        column-gap: calc(${container.colGap} * ${props => props.theme.base.spacing});
      `}

      ${container.rowGap !== undefined &&
      css`
        row-gap: calc(${container.rowGap} * ${props => props.theme.base.spacing});
      `}
    `;
  }
};

const getItemStyles = (item: FlexProps['item']) => {
  return css`
    ${item &&
    css`
      max-width: 100%;
      min-width: 0;

      ${item.grow !== undefined &&
      css`
        flex-grow: ${item.grow};
      `}

      ${item.shrink !== undefined &&
      css`
        flex-shrink: ${item.shrink};
      `}

      ${item.alignSelf &&
      css`
        align-self: ${prefix(item.alignSelf)};
      `}

      ${item.basis &&
      css`
        flex-basis: ${item.basis};
      `}
    `}
  `;
};

const breakpointOrder = ['xs', 'sm', 'md', 'lg', 'xl'] as const;

export const StyledFlex = styled.div<FlexProps>(
  ({
    container,
    item,
    theme: {
      base: { breakpoints: themeBreakpoints }
    },
    xs,
    sm,
    md,
    lg,
    xl
  }) => {
    const breakpoints = {
      xs,
      sm,
      md,
      lg,
      xl
    };

    return css`
      ${getContainerStyles(container)}
      ${getItemStyles(item)}

    ${breakpointOrder.map(
        breakpoint =>
          breakpoints[breakpoint] &&
          css`
            @media screen and (min-width: ${themeBreakpoints[breakpoint]}) {
              ${getContainerStyles(breakpoints[breakpoint]?.container)}
              ${getItemStyles(breakpoints[breakpoint]?.item)}
            }
          `
      )}
    `;
  }
);

StyledFlex.defaultProps = defaultThemeProp;

const Flex: FunctionComponent<FlexProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<FlexProps>, ref: FlexProps['ref']) => {
    return <StyledFlex {...props} ref={ref} />;
  }
);

export default Flex;
